<template>
  <div ref="rootEl" class="relative inline-block">
    <!-- Badge trigger -->
    <button
      type="button"
      class="select-none"
      aria-haspopup="menu"
      :aria-expanded="isOpen ? 'true' : 'false'"
      @click="toggle"
    >
      <div :class="outerClass">
        <div class="relative">
          <!-- Avatar -->
          <div
            class="bg-[var(--chat-input-bg)] text-[var(--chat-input-bg)] rounded-full border border-[var(--color-mode-border)] flex items-center justify-center aspect-square"
            :class="avatarClass"
            aria-hidden="true"
          >
            <div class="relative w-10 h-10">
              <svg viewBox="0 0 48 48" class="w-10 h-10" aria-hidden="true">
                <circle cx="24" cy="24" r="24" fill="currentColor" />
              </svg>
              <span
                class="absolute inset-0 grid place-items-center text-[var(--color-mode-text)] font-[neulis-sans] leading-none"
                :class="initialClass"
              >
                {{ initial }}
              </span>
            </div>
          </div>

          <!-- Tier badge -->
          <div
            class="absolute z-10 flex items-center justify-center gap-1 min-w-[40px] px-2 py-2 rounded-[40px] text-sm whitespace-nowrap leading-none ring-4 ring-[var(--sidebar-background)]"
            :class="badgePosClass"
            :style="badgeStyle"
          >
            {{ subscriptionName }}
          </div>
        </div>
      </div>
    </button>

    <!-- Popup -->
    <div
      v-if="isOpen"
      class="absolute z-50"
      :class="popupPosClass"
      role="menu"
      aria-label="Profile menu"
    >
      <div
        class="flex w-[303px] min-w-[303px] p-4 flex-col justify-center items-start gap-4 rounded-xl bg-[var(--chat-input-bg)] border border-[var(--color-mode-border)]"
      >
        <!-- Group 1 -->
        <div class="w-full flex flex-col gap-2">
          <!-- TEMP: uitgecommenteerd zolang deze acties nog niet actief zijn.
          <UiButton
            type="button"
            icon="custom-icons:settings"
            icon-position="left"
            icon-size="md"
            icon-color="var(--color-mode-icon)"
            disabled
            class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-[var(--color-mode-text)] hover:bg-[var(--profile-menu-hover)] opacity-20 cursor-not-allowed pointer-events-none"
          >
            <span class="text-base">Instellingen</span>
          </UiButton>

          <UiButton
            type="button"
            icon="custom-icons:upgrade"
            icon-position="left"
            icon-size="md"
            icon-color="var(--color-mode-icon)"
            disabled
            class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-[var(--color-mode-text)] hover:bg-[var(--profile-menu-hover)] opacity-20 cursor-not-allowed pointer-events-none"
          >
            <span class="text-base">Upgrade</span>
          </UiButton>

          <UiButton
            type="button"
            icon="custom-icons:feedback"
            icon-position="left"
            icon-size="md"
            icon-color="var(--color-mode-icon)"
            disabled
            class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-[var(--color-mode-text)] hover:bg-[var(--profile-menu-hover)] opacity-20 cursor-not-allowed pointer-events-none"
          >
            <span class="text-base">Feedback</span>
          </UiButton>
          -->

          <UiButton
            to="/info/aanvragen"
            icon="custom-icons:feedback"
            icon-position="left"
            icon-size="md"
            icon-color="var(--color-mode-icon)"
            class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-[var(--color-mode-text)] hover:bg-[var(--profile-menu-hover)]"
            @click="close"
          >
            <span class="text-base">Contactformulier</span>
          </UiButton>

          <UiButton
            to="https://www.violet88.nl/"
            target="_blank"
            rel="noopener noreferrer"
            external
            icon="custom-icons:upgrade"
            icon-position="left"
            icon-size="md"
            icon-color="var(--color-mode-icon)"
            class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-[var(--color-mode-text)] hover:bg-[var(--profile-menu-hover)]"
            @click="close"
          >
            <span class="text-base">Violet88</span>
          </UiButton>
        </div>

        <!-- Divider -->
        <div class="w-full h-px bg-[#DCDCDC]" />

        <!-- Group 2 -->
        <div class="w-full flex flex-col gap-2">
          <!-- TEMP: Help button disabled while tawk.to is turned off.
          <UiButton
            type="button"
            icon="custom-icons:help"
            icon-position="left"
            icon-size="md"
            icon-color="var(--color-mode-icon)"
            disabled
            class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-[var(--color-mode-text)] hover:bg-[var(--profile-menu-hover)] opacity-20 cursor-not-allowed pointer-events-none"
          >
            <span class="text-base">Hulp</span>
          </UiButton>
          -->

          <UiButton
            type="button"
            icon="custom-icons:logout"
            icon-position="left"
            icon-size="md"
            icon-color="var(--color-mode-icon)"
            class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-[var(--color-mode-text)] hover:bg-[var(--profile-menu-hover)]"
            @click="onAction('logout')"
          >
            <span class="text-base">Uitloggen</span>
          </UiButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, computed } from "vue";

type Variant = "expanded" | "collapsed";
type MenuAction = "settings" | "upgrade" | "feedback" | "help" | "logout";

const emit = defineEmits<{
  (e: "action", action: MenuAction): void;
}>();

const props = defineProps<{
  username: string;
  subscriptionName: string;
  variant: Variant;
}>();

const isOpen = ref(false);
const rootEl = ref<HTMLElement | null>(null);

const toggle = () => (isOpen.value = !isOpen.value);
const close = () => (isOpen.value = false);

const onAction = (action: MenuAction) => {
  emit("action", action);
  close();
};

function onDocumentMousedown(e: MouseEvent) {
  if (!isOpen.value) return;
  const el = rootEl.value;
  if (!el) return;
  if (e.target instanceof Node && !el.contains(e.target)) close();
}

function onEscape(e: KeyboardEvent) {
  if (!isOpen.value) return;
  if (e.key === "Escape") close();
}

onMounted(() => {
  document.addEventListener("mousedown", onDocumentMousedown);
  window.addEventListener("keydown", onEscape);
});
onBeforeUnmount(() => {
  document.removeEventListener("mousedown", onDocumentMousedown);
  window.removeEventListener("keydown", onEscape);
});

// ---- badge logic ----
const initial = computed(() => (props.username?.[0] ?? "?").toUpperCase());
const initialClass = computed(() => "text-[24px]");

const outerClass = computed(() => {
  return props.variant === "collapsed"
    ? "flex flex-col items-center"
    : "flex items-center";
});

const badgePosClass = computed(() => {
  return props.variant === "collapsed"
    ? "left-[calc(100%-10px)] top-1/2 -translate-y-1/2 md:left-1/2 md:top-full md:-translate-x-1/2 md:-translate-y-1/3"
    : "left-[calc(100%-10px)] top-1/2 -translate-y-1/2";
});

const avatarClass = computed(() =>
  props.variant === "collapsed" ? "w-[48px] h-[48px] p-2" : "w-12 h-12",
);

const popupPosClass = computed(() => "left-0 bottom-full mb-4");

/**
 * Tier styles (exact design gradients)
 */
const badgeStyle = computed(() => {
  const key = (props.subscriptionName ?? "free").trim().toLowerCase();

  const map: Record<string, { bg: string; color?: string; shadow?: string }> = {
    free: {
      bg: "linear-gradient(180deg, #373B44 0%, #4286F4 100%)",
      color: "#fff",
      shadow: "0 8px 20px rgba(66, 134, 244, 0.25)",
    },
    starter: {
      bg: "linear-gradient(180deg, #11998E 0%, #38EF7D 100%)",
      color: "#fff",
      shadow: "0 8px 20px rgba(56, 239, 125, 0.25)",
    },
    pro: {
      bg: "linear-gradient(180deg, #8C1E70 0%, #9747FF 100%)",
      color: "#fff",
      shadow: "0 8px 20px rgba(151, 71, 255, 0.25)",
    },
    team: {
      bg: "linear-gradient(180deg, #8E2DE2 0%, #4A00E0 100%)",
      color: "#fff",
      shadow: "0 8px 20px rgba(74, 0, 224, 0.25)",
    },
    enterprise: {
      bg: "linear-gradient(180deg, #F799D3 0%, #6F7BF7 100%)",
      color: "#fff",
      shadow: "0 8px 20px rgba(111, 123, 247, 0.25)",
    },
  };

  const s = map[key];

  if (!s) {
    return {
      background: "transparent",
      color: "var(--color-mode-text)",
      border: "1px solid var(--color-mode-text)",
    } as Record<string, string>;
  }

  return {
    background: s.bg,
    color: s.color ?? "#fff",
    boxShadow: s.shadow ?? "none",
  } as Record<string, string>;
});
</script>
